import React, { useState, useEffect } from 'react';
import { useGameStore } from '../../store/game-store.js';
import { loadData, exportData, importData } from '../../lib/storage.js';
import { tmdb, imgUrl } from '../../lib/tmdb.js';
import { GENRE_NAME } from '../../lib/genres.js';
import './StatsView.css';

export default function StatsView() {
  const setView = useGameStore(s => s.setView);
  const [data, setData] = useState(loadData());
  const [tab, setTab] = useState('overview'); // overview | collector | affinity
  const [topActors, setTopActors] = useState([]);
  const [importText, setImportText] = useState('');
  const [showImport, setShowImport] = useState(false);

  // Load top actors with names
  useEffect(() => {
    const loadTopActors = async () => {
      const entries = Object.entries(data.affinity.actors || {})
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10);

      const actors = await Promise.all(
        entries.map(async ([id, count]) => {
          try {
            const person = await tmdb(`/person/${id}`);
            return {
              id: parseInt(id, 10),
              name: person.name,
              image: imgUrl(person.profile_path, 'sm'),
              count,
            };
          } catch {
            return { id: parseInt(id, 10), name: `Actor #${id}`, image: '', count };
          }
        })
      );
      setTopActors(actors);
    };

    if (tab === 'affinity') loadTopActors();
  }, [tab, data]);

  const handleExport = () => {
    const json = exportData();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'connect-the-stars-data.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    if (importData(importText)) {
      setData(loadData());
      setShowImport(false);
      setImportText('');
    }
  };

  const collectorActors = Object.keys(data.collector.actors || {}).length;
  const collectorMovies = Object.keys(data.collector.movies || {}).length;
  const collectorTv = Object.keys(data.collector.tv || {}).length;

  // Genre breakdown
  const genreBreakdown = Object.entries(data.affinity.genres || {})
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([id, count]) => ({ name: GENRE_NAME[id] || `Genre ${id}`, count }));

  return (
    <div className="stats">
      <header className="stats__header">
        <button className="stats__back" onClick={() => setView('home')}>
          &#x2190; Back
        </button>
        <h2 className="stats__title">Stats & Collection</h2>
      </header>

      <div className="stats__tabs">
        <button
          className={`stats__tab ${tab === 'overview' ? 'stats__tab--active' : ''}`}
          onClick={() => setTab('overview')}
        >Overview</button>
        <button
          className={`stats__tab ${tab === 'collector' ? 'stats__tab--active' : ''}`}
          onClick={() => setTab('collector')}
        >Collection</button>
        <button
          className={`stats__tab ${tab === 'affinity' ? 'stats__tab--active' : ''}`}
          onClick={() => setTab('affinity')}
        >Affinity</button>
      </div>

      {tab === 'overview' && (
        <div className="stats__content">
          <div className="stats__overview-grid">
            <div className="stats__stat">
              <span className="stats__stat-value">{data.stats.gamesPlayed}</span>
              <span className="stats__stat-label">Games Played</span>
            </div>
            <div className="stats__stat">
              <span className="stats__stat-value">{data.stats.gamesWon}</span>
              <span className="stats__stat-label">Games Won</span>
            </div>
            <div className="stats__stat">
              <span className="stats__stat-value">{data.daily.streak}</span>
              <span className="stats__stat-label">Daily Streak</span>
            </div>
            <div className="stats__stat">
              <span className="stats__stat-value">{data.daily.maxStreak}</span>
              <span className="stats__stat-label">Best Streak</span>
            </div>
            <div className="stats__stat">
              <span className="stats__stat-value">{data.stats.totalHintsUsed}</span>
              <span className="stats__stat-label">Hints Used</span>
            </div>
            <div className="stats__stat">
              <span className="stats__stat-value">
                {collectorActors + collectorMovies + collectorTv}
              </span>
              <span className="stats__stat-label">Collection</span>
            </div>
          </div>

          <div className="stats__data-actions">
            <button className="stats__data-btn" onClick={handleExport}>
              Export Data
            </button>
            <button className="stats__data-btn" onClick={() => setShowImport(!showImport)}>
              Import Data
            </button>
          </div>

          {showImport && (
            <div className="stats__import">
              <textarea
                className="stats__import-input"
                placeholder="Paste exported JSON here..."
                value={importText}
                onChange={e => setImportText(e.target.value)}
                rows={4}
              />
              <button className="stats__import-btn" onClick={handleImport}>
                Import
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'collector' && (
        <div className="stats__content">
          <div className="stats__collector-summary">
            <span>{collectorActors} Stars</span>
            <span>{collectorMovies} Films</span>
            <span>{collectorTv} TV Shows</span>
          </div>
          <p className="stats__collector-note">
            Every unique actor and title in your winning paths earns a card here.
            Keep playing to grow your collection.
          </p>
        </div>
      )}

      {tab === 'affinity' && (
        <div className="stats__content">
          {topActors.length > 0 && (
            <div className="stats__affinity-section">
              <h3 className="stats__section-title">Most-Used Stars</h3>
              <div className="stats__affinity-list">
                {topActors.map(actor => (
                  <div key={actor.id} className="stats__affinity-item">
                    {actor.image && (
                      <img src={actor.image} alt={actor.name} className="stats__affinity-img" />
                    )}
                    <span className="stats__affinity-name">{actor.name}</span>
                    <span className="stats__affinity-count">{actor.count}x</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {genreBreakdown.length > 0 && (
            <div className="stats__affinity-section">
              <h3 className="stats__section-title">Genre Breakdown</h3>
              <div className="stats__genre-bars">
                {genreBreakdown.map(g => (
                  <div key={g.name} className="stats__genre-bar">
                    <span className="stats__genre-name">{g.name}</span>
                    <div className="stats__genre-track">
                      <div
                        className="stats__genre-fill"
                        style={{ width: `${(g.count / genreBreakdown[0].count) * 100}%` }}
                      />
                    </div>
                    <span className="stats__genre-count">{g.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
